import { t } from '@framework/i18n';
import { yup } from '@framework/validation';
import { RuleType } from '../../IRule';
import { IRuleType } from '../../IRuleType';
import { UniqueFieldRuleComponent } from './UniqueFieldRuleComponent';

const SingleUniqueFieldSchema = yup.object({
  field: yup.string().required().label('Campo figlio'),
  customMessage: yup
    .string()
    .notRequired()
    .label('Messaggio personalizzato di validazione'),
});

export type IUniqueSingleFieldRule = yup.InferType<
  typeof SingleUniqueFieldSchema
>;

const UniqueFieldRuleSchema = yup
  .object({
    fields: yup
      .array(SingleUniqueFieldSchema)
      .notRequired()
      .label('Campi unici'),
  })
  .notRequired()
  .noUnknown();

export type IUniqueFieldRuleType = yup.InferType<typeof UniqueFieldRuleSchema>;

/**
 * Funzione che permette di ottenere la label di un campo di una lista ripetibile.
 */
function getInnerSchemaLabel(
  arraySchema: any,
  fieldName: string,
): string | undefined {
  return arraySchema.innerType?.fields?.[fieldName]?.spec?.label;
}

/**
 * Regola che permette di validare un campo di una lista ripetibile
 * come "unico". In questo modo, non è possibile impostare lo stesso
 * valore per più campi della lista.
 */
export const UniqueFieldRule: IRuleType<IUniqueFieldRuleType> = {
  type: RuleType.UniqueField,
  name: 'Campi unici',
  configComponent: UniqueFieldRuleComponent,
  configValidator: UniqueFieldRuleSchema,
  validator: (schema, rule) => {
    rule?.fields?.forEach(field => {
      schema = schema.test({
        name: 'unique-field',
        exclusive: false,
        skipAbsent: true,
        message:
          field?.customMessage ??
          t(
            'rules.unique-field-rule.error',
            'Il campo {{label}} deve essere univoco',
            { label: getInnerSchemaLabel(schema, field.field) ?? field.field },
          ),
        test(value: Array<Record<string, any>>) {
          if (value == null) return true;
          const targets = value.map(v => v[field.field]).filter(v => v != null);
          return targets.length === new Set(targets).size;
        },
      });
    });

    return schema;
  },
};
